import type { ChatUi } from '@voll-intelligence/genui-contract';
import type { GenUiDispatch } from '../../shared/ui/action.js';
import { useGenUiI18n } from '../../shared/ui/i18n.js';
import type { GenUiEntry } from '../../shared/ui/registry.js';
import { BusPlannerCard, busWhenLabel } from './ui/bus_planner.js';

type BusPlannerUi = Extract<ChatUi, { kind: 'bus_planner' }>;

/**
 * A busca volta por `interaction` (o challenge do card + a `BusPlannerSearch`):
 * o agente busca com as rodoviárias do card, sem o LLM reinterpretar o texto. O
 * eco é a fala que a pessoa aparenta ter dito — por isso é traduzido aqui, num
 * componente (o `render` do catálogo não roda hook).
 */
function BusPlannerEntry({
  ui,
  note,
  readOnly,
  superseded,
  replay,
  dispatch,
}: Readonly<{
  ui: BusPlannerUi;
  note?: string;
  readOnly?: boolean;
  superseded?: boolean;
  replay?: boolean;
  dispatch: GenUiDispatch;
}>) {
  const { t, locale } = useGenUiI18n();
  return (
    <BusPlannerCard
      ui={ui}
      {...(note ? { note } : {})}
      {...(readOnly ? { readOnly } : {})}
      {...(superseded ? { superseded } : {})}
      {...(replay ? { replay } : {})}
      onSearch={(search) => {
        dispatch({
          type: 'interaction',
          interactionId: ui.challengeId,
          text: t('busSearch.searchText', {
            from: search.from.name,
            to: search.to.name,
            when: busWhenLabel(
              { from: search.outwardDate, ...(search.returnDate ? { to: search.returnDate } : {}) },
              locale,
            ),
          }),
          data: search,
        });
      }}
    />
  );
}

/** Entrada de catálogo do `bus_planner` (slot chat). */
export const busPlannerEntry: GenUiEntry<BusPlannerUi> = {
  kind: 'bus_planner',
  slot: 'chat',
  policy: { productOf: () => 'bus' },
  render: ({ ui, note, readOnly, superseded, replay, dispatch }) => (
    <BusPlannerEntry
      ui={ui}
      {...(note ? { note } : {})}
      {...(readOnly ? { readOnly } : {})}
      {...(superseded ? { superseded } : {})}
      {...(replay ? { replay } : {})}
      dispatch={dispatch}
    />
  ),
};
